import React, { useState } from 'react';
import {
  Box,
  Button,
  Popover,
  Typography,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  alpha,
  useTheme,
  Grow,
  Divider,
} from '@mui/material';
import { renderIcon } from '../../../../helpers/renderDynamicIcons';
import { useTranslation } from 'react-i18next';
import { ExpandMore } from '@mui/icons-material';

const ModernMinimalTemplate = ({ 
  items, 
  onItemClick, 
  flatNavigationItems 
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [activeItem, setActiveItem] = useState(null);
  const [hoveredButton, setHoveredButton] = useState(null);
  const [t] = useTranslation();
  const theme = useTheme();

  const getChildren = (item) => {
    if (item.children && Array.isArray(item.children)) {
      return item.children;
    }
    if (item.hasChildren && flatNavigationItems.length > 0) {
      return flatNavigationItems.filter(child => child.parentId === item.id);
    }
    return [];
  };

  const hasChildren = (item) => {
    const children = getChildren(item);
    return children.length > 0;
  };

  const handleClick = (event, item) => {
    if (hasChildren(item)) {
      setAnchorEl(event.currentTarget);
      setActiveItem(item);
    } else {
      onItemClick(item);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
    setActiveItem(null);
  };

  const handleChildClick = (child) => {
    onItemClick(child);
    handleClose();
  };

  const isOpen = Boolean(anchorEl);

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      {items?.filter(item => !item.parentId).map((item, index) => {
        const itemHasChildren = hasChildren(item);
        const isHovered = hoveredButton === item.id;
        const isActive = activeItem?.id === item.id;

        return (
          <Box
            key={item.id}
            sx={{ position: 'relative' }}
            onMouseEnter={() => setHoveredButton(item.id)}
            onMouseLeave={() => setHoveredButton(null)}
          >
            {/* Hover Background Effect */}
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                borderRadius: 3,
                background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.15)} 0%, ${alpha(theme.palette.secondary.main, 0.1)} 100%)`,
                opacity: isHovered || isActive ? 1 : 0,
                transition: 'opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                pointerEvents: 'none',
              }}
            />

            <Button
              onClick={(e) => handleClick(e, item)}
              onMouseEnter={(e) => {
                if (itemHasChildren && !isOpen) {
                  handleClick(e, item);
                }
              }}
              sx={{
                position: 'relative',
                px: 2.5,
                py: 1.25,
                borderRadius: 3,
                textTransform: 'none',
                color: isActive ? theme.palette.primary.main : theme.palette.text.primary,
                fontWeight: isActive ? 600 : 500,
                fontSize: '0.95rem',
                minHeight: 44,
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  backgroundColor: 'transparent',
                  color: theme.palette.primary.main,
                  transform: 'translateY(-2px)',
                },
              }}
            >
              {/* Icon with Animation */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  position: 'relative',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 24,
                    height: 24,
                    transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    transform: isHovered ? 'scale(1.1) rotate(-5deg)' : 'scale(1) rotate(0deg)',
                  }}
                >
                  {renderIcon(item.icon, {
                    sx: { 
                      fontSize: 20,
                      transition: 'color 0.3s ease',
                    }
                  })}
                </Box>

                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 'inherit',
                    letterSpacing: 0.2,
                  }}
                >
                  {t(item.name)}
                </Typography>

                {itemHasChildren && (
                  <ExpandMore
                    sx={{
                      fontSize: 18,
                      ml: 0.5,
                      transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      transform: isOpen && isActive ? 'rotate(180deg)' : 'rotate(0deg)',
                    }}
                  />
                )}
              </Box>

              {/* Active Indicator */}
              <Box
                sx={{
                  position: 'absolute',
                  bottom: 0,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: isActive ? '60%' : '0%',
                  height: 3,
                  borderRadius: '3px 3px 0 0',
                  background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                  transition: 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                }}
              />
            </Button>
          </Box>
        );
      })}

      {/* Glassmorphism Popover */}
      <Popover
        open={isOpen}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        TransitionComponent={Grow}
        transitionDuration={300}
        disableRestoreFocus
        slotProps={{
          paper: {
            sx: {
              mt: 1.5,
              borderRadius: 3,
              minWidth: 280,
              maxWidth: 400,
              background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.95)} 0%, ${alpha(theme.palette.background.paper, 0.9)} 100%)`,
              backdropFilter: 'blur(20px) saturate(180%)',
              border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
              boxShadow: `
                0 8px 32px ${alpha(theme.palette.common.black, 0.12)},
                0 2px 8px ${alpha(theme.palette.common.black, 0.08)},
                inset 0 1px 0 ${alpha(theme.palette.common.white, 0.1)}
              `,
              overflow: 'visible',
              '&:before': {
                content: '""',
                position: 'absolute',
                top: -8,
                left: 24,
                width: 16,
                height: 16,
                transform: 'rotate(45deg)',
                background: alpha(theme.palette.background.paper, 0.95),
                backdropFilter: 'blur(20px)',
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                borderRight: 'none',
                borderBottom: 'none',
              },
            },
          },
        }}
        onMouseLeave={handleClose}
      >
        <Box
          sx={{ py: 1 }}
          onMouseEnter={() => setAnchorEl(anchorEl)}
        >
          {/* Header */}
          {activeItem && (
            <Box sx={{ px: 2, py: 1.5, mb: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: 2,
                    background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.2)} 0%, ${alpha(theme.palette.secondary.main, 0.15)} 100%)`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {renderIcon(activeItem.icon, {
                    sx: { 
                      fontSize: 22,
                      color: theme.palette.primary.main 
                    }
                  })}
                </Box>
                <Typography 
                  variant="subtitle1" 
                  fontWeight={600}
                  sx={{
                    background: `linear-gradient(135deg, ${theme.palette.text.primary} 0%, ${alpha(theme.palette.text.primary, 0.8)} 100%)`,
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                  }}
                >
                  {t(activeItem.name)}
                </Typography>
              </Box>
            </Box>
          )}

          <Divider sx={{ 
            mx: 2, 
            mb: 1,
            background: `linear-gradient(90deg, transparent 0%, ${alpha(theme.palette.divider, 0.3)} 50%, transparent 100%)`,
            height: 1,
            border: 'none'
          }} />

          {/* Children List */}
          <List sx={{ py: 0.5 }}>
            {activeItem && getChildren(activeItem).map((child, index) => (
              <ListItemButton
                key={child.id}
                onClick={() => handleChildClick(child)}
                sx={{
                  mx: 1,
                  borderRadius: 2,
                  mb: 0.5,
                  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.primary.main, 0.08),
                    transform: 'translateX(4px)',
                    '& .MuiListItemIcon-root': {
                      transform: 'scale(1.1)',
                      color: theme.palette.primary.main,
                    },
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 40,
                    transition: 'all 0.2s ease',
                  }}
                >
                  {renderIcon(child.icon, {
                    sx: { fontSize: 20 }
                  })}
                </ListItemIcon>
                <ListItemText
                  primary={t(child.name)}
                  secondary={child.description ? t(child.description) : null}
                  primaryTypographyProps={{
                    variant: 'body2',
                    fontWeight: 500,
                  }}
                  secondaryTypographyProps={{
                    variant: 'caption',
                    sx: { 
                      mt: 0.5,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: '-webkit-box',
                      WebkitLineClamp: 1,
                      WebkitBoxOrient: 'vertical',
                    }
                  }}
                />
              </ListItemButton>
            ))}
          </List>
        </Box>
      </Popover>
    </Box>
  );
};

export default ModernMinimalTemplate;
